import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { ChoiceGroup } from "@fluentui/react/lib/ChoiceGroup";
import { IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";
import { TextField } from "@fluentui/react/lib/TextField";

import Modal from "../../../../../components/modal/Modal";
import { FolderListing, InstancesListing, InstancesListingPopUp } from "../components";

import { TEXT } from "../../../../../shared/constants/texts";
import { INITIAL_FORM_DATA } from "../../../redux/slice";
import { VIEW_FOLDER_AND_DOCUMENTS } from "../../../../../shared/constants/addToICIToggleView";
import { PAGE, PAGE_SIZE } from "../../../../../shared/constants/paginationConstants";
import { logError } from "../../../../../shared/services/errorLogging";
import { bactInstanceApiCall, instanceApiCall } from "../../../redux/apiCalls";
import { updateEmailThreadFormData } from "../../../utilFunctions";

const EmailThreadUpload: React.FC = () => {
  const state = useSelector((state: any) => state.addToIciSlice);
  const { singleEmailData, uploadSingleEmailThreadData } = state;

  const subject = `(Subject - ${singleEmailData.subject || ""})`;

  return (
    <div className="email-thread-upload-add-to-ici">
      <p className="email-thread">{TEXT.EMAIL_THREAD}</p>
      <p className="subject">{subject}</p>
      {!uploadSingleEmailThreadData ? <EmailThreadUploadFromEmpty /> : null}
      {uploadSingleEmailThreadData && uploadSingleEmailThreadData.Status === TEXT.SUCCESS ? (
        <EmailThreadUploadSuccess />
      ) : null}
      {uploadSingleEmailThreadData && uploadSingleEmailThreadData.Status === TEXT.ALREADY_UPLOADED ? (
        <EmailThreadUploadAlreadyUploaded />
      ) : null}
      {(uploadSingleEmailThreadData && uploadSingleEmailThreadData.Status === TEXT.FAILED) ||
      (uploadSingleEmailThreadData && uploadSingleEmailThreadData.Status === TEXT.FAILURE) ? (
        <EmailThreadUploadFailed />
      ) : null}
    </div>
  );
};

export default EmailThreadUpload;

const EmailThreadUploadSuccess = () => {
  const state = useSelector((state: any) => state.addToIciSlice);
  const { selectedAssociation, defaultAssociationOfAgreement } = state;

  const name = selectedAssociation
    ? selectedAssociation.name
    : defaultAssociationOfAgreement
      ? defaultAssociationOfAgreement.name
      : "";

  return (
    <div className="email-thread-upload-success-single-email-upload">
      <p>
        {TEXT.SUCCESSFULLY_UPLOADED_EMAIL_THREAD}
        {name}
        {TEXT.DOT}
      </p>
    </div>
  );
};

const EmailThreadUploadFailed = () => {
  const state = useSelector((state: any) => state.addToIciSlice);
  const { selectedAssociation, defaultAssociationOfAgreement } = state;

  const name = selectedAssociation
    ? selectedAssociation.name
    : defaultAssociationOfAgreement
      ? defaultAssociationOfAgreement.name
      : "";

  return (
    <div className="email-thread-upload-failed-single-email-upload">
      <p>
        {TEXT.UPLOAD_FAILED_EMAIL_THREAD} {name}
        {TEXT.DOT}
      </p>
    </div>
  );
};

const EmailThreadUploadAlreadyUploaded = () => {
  const state = useSelector((state: any) => state.addToIciSlice);
  const { uploadSingleEmailThreadData, associationData, emailThreadFormData } = state;
  const { AssociationDetailsOnDragDrop } = uploadSingleEmailThreadData;

  const onUploadChangeType = (_e, option) => {
    if (option.key === TEXT.REPLACE_EXISTING) {
      const { AssociationName, FolderReference, InstanceId, InstanceName, InstanceAgreementCode } =
        uploadSingleEmailThreadData.AssociationDetailsOnDragDrop;
      const replaceInstance = {
        SysId: InstanceId,
        Name: InstanceName,
        ICMAgreementCode: InstanceAgreementCode,
        folderReference: FolderReference,
      };
      const association = associationData.filter((association) => association.Name === AssociationName);
      const updatedEmailThreadFormData = {
        ...INITIAL_FORM_DATA,
        selectedUploadType: TEXT.REPLACE_EXISTING,
        selectedAssociation: association[0],
        selectedFolderReplaceExisting: FolderReference ? FolderReference : null,
        replaceAssociationInstance: !FolderReference ? replaceInstance : null,
        replaceFolderInstance: FolderReference ? replaceInstance : null,
      };
      updateEmailThreadFormData(updatedEmailThreadFormData);
    }
    if (option.key === TEXT.KEEP_BOTH) {
      const { AssociationName, FolderReference, InstanceName } =
        uploadSingleEmailThreadData.AssociationDetailsOnDragDrop;
      const association = associationData.filter((association) => association.Name === AssociationName);
      const updatedEmailThreadFormData = {
        ...INITIAL_FORM_DATA,
        selectedUploadType: TEXT.KEEP_BOTH,
        selectedAssociation: association[0],
        newDocumentName: InstanceName,
        selectedFolderNewDocument: FolderReference ? FolderReference : null,
      };
      updateEmailThreadFormData(updatedEmailThreadFormData);
    }
    if (option.key === TEXT.SKIP) {
      const updatedEmailThreadFormData = {
        ...INITIAL_FORM_DATA,
        selectedUploadType: TEXT.SKIP,
      };
      updateEmailThreadFormData(updatedEmailThreadFormData);
    }
  };

  const uploadOptions: IChoiceGroupOption[] = [
    {
      key: TEXT.REPLACE_EXISTING,
      text: TEXT.REPLACE_EXISTING,
      onRenderLabel: (p) => (
        <span id={p.id + TEXT.REPLACE_EXISTING} className="ms-ChoiceFieldLabel" style={uploadOptionsRenderLabelStyle}>
          {p.text}
        </span>
      ),
      styles: uploadOptionsStyle,
    },
    {
      key: TEXT.KEEP_BOTH,
      text: TEXT.KEEP_BOTH,
      onRenderLabel: (p) => (
        <span id={p.id + TEXT.KEEP_BOTH} className="ms-ChoiceFieldLabel" style={uploadOptionsRenderLabelStyle}>
          {p.text}
        </span>
      ),
      styles: uploadOptionsStyle,
    },
    {
      key: TEXT.SKIP,
      text: TEXT.SKIP,
      onRenderLabel: (p) => (
        <span id={p.id + TEXT.SKIP} className="ms-ChoiceFieldLabel" style={uploadOptionsRenderLabelStyle}>
          {p.text}
        </span>
      ),
      styles: uploadOptionsStyle,
    },
  ];

  let association = null;
  let name = "";
  if (AssociationDetailsOnDragDrop && AssociationDetailsOnDragDrop.AssociationName) {
    association = associationData.find((item) => item.Name === AssociationDetailsOnDragDrop.AssociationName);
    name = association.name;
  }
  return (
    <div className="email-thread-upload-already-uploaded-single-email-upload">
      <p>
        {TEXT.ALREADY_EXISTS_IN} {name}
      </p>
      <div className="already-uploaded-options-container-single-email-upload">
        <div className="choice-group-container-already-uploaded">
          <ChoiceGroup
            options={uploadOptions}
            onChange={onUploadChangeType}
            selectedKey={emailThreadFormData.selectedType}
          />
        </div>
      </div>
    </div>
  );
};

const EmailThreadUploadFromEmpty = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.addToIciSlice);
  const { associationData, emailThreadFormData, selectedViewFolderAndDocuments, singleEmailData, selectedAgreement } =
    state;
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };
  const isFolderView = selectedViewFolderAndDocuments.id === VIEW_FOLDER_AND_DOCUMENTS[1].id;

  const getInstances = async (data) => {
    const emailThreadData = { ...data };
    const { selectedAssociation } = emailThreadData;
    let errorMsg = errorValidationMsg.ERROR_LOADING_ASSOCIATION_INSTANCES;
    let pageSize = PAGE_SIZE.ASSOCIATION_INSTANCES_DATA_SINGLE_EMAIL_UPLOAD;
    if (isFolderView) {
      errorMsg = errorValidationMsg.ERROR_LOADING_FOLDER_INSTANCES;
      pageSize = PAGE_SIZE.FOLDER_INSTANCES_DATA_SINGLE_EMAIL_UPLOAD;
    }
    if (selectedAssociation.EnableBulkProcessing) {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        agreementCT: selectedAgreement.entityName,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: pageSize,
        errMsg: errorMsg,
      };
      const instanceResponse = await bactInstanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getInstances(data),
      });
      const { response, error } = instanceResponse;
      if (response) {
        let updatedEmailThreadFormData = { ...emailThreadData };
        if (!isFolderView) {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            associationInstances: response,
            isLoadingAssociationInstances: false,
          };
        } else {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            folderInstances: response,
            isLoadingFolderInstances: false,
          };
        }
        updateEmailThreadFormData(updatedEmailThreadFormData);
      }
      if (error) {
        let updatedEmailThreadFormData = { ...emailThreadData };
        if (!isFolderView) {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            associationInstances: [],
            isLoadingAssociationInstances: false,
          };
        } else {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            folderInstances: [],
            isLoadingFolderInstances: false,
          };
        }
        updateEmailThreadFormData(updatedEmailThreadFormData);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|email-thread-upload|EmailThreadUpload - getInstances",
            callerApi:
              "api/Instance/GetFilteredBACTInstances?entityName=&parentEntityName=&parentId=&pageNumber=&pageSize=",
          },
        });
      }
    } else {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: pageSize,
        errMsg: errorMsg,
      };
      const instanceResponse = await instanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getInstances(data),
      });
      const { response, error } = instanceResponse;
      if (response) {
        let updatedEmailThreadFormData = { ...emailThreadData };
        if (!isFolderView) {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            associationInstances: response,
            isLoadingAssociationInstances: false,
          };
        } else {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            folderInstances: response,
            isLoadingFolderInstances: false,
          };
        }
        updateEmailThreadFormData(updatedEmailThreadFormData);
      }
      if (error) {
        let updatedEmailThreadFormData = { ...emailThreadData };
        if (!isFolderView) {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            associationInstances: [],
            isLoadingAssociationInstances: false,
          };
        } else {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            folderInstances: [],
            isLoadingFolderInstances: false,
          };
        }
        updateEmailThreadFormData(updatedEmailThreadFormData);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|email-thread-upload|EmailThreadUpload - getInstances",
            callerApi: "api/Agreement/FindAgreements?contractType=&page=&pageSize=",
          },
        });
      }
    }
  };

  const getInstancesPopup = async (data, searchTerm = "") => {
    const emailThreadData = { ...data };
    const { selectedAssociation } = emailThreadData;
    let errorMsg = errorValidationMsg.ERROR_LOADING_ASSOCIATION_INSTANCES;
    let pageSize = PAGE_SIZE.INSTANCES_SHOW_MORE_POPUP;
    if (isFolderView) {
      errorMsg = errorValidationMsg.ERROR_LOADING_FOLDER_INSTANCES;
      pageSize = PAGE_SIZE.INSTANCES_SHOW_MORE_POPUP;
    }
    if (selectedAssociation.EnableBulkProcessing) {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        agreementCT: selectedAgreement.entityName,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: pageSize,
        errMsg: errorMsg,
        searchTerm: searchTerm,
      };
      const instanceResponse = await bactInstanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getInstances(data),
      });
      const { response, error } = instanceResponse;
      if (response) {
        let updatedEmailThreadFormData = { ...emailThreadData };
        if (!isFolderView) {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            associationInstancesPopUp: response,
            isLoadingAssociationInstancesPopUp: false,
          };
        } else {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            folderInstancesPopUp: response,
            isLoadingFolderInstancesPopUp: false,
          };
        }
        updateEmailThreadFormData(updatedEmailThreadFormData);
      }
      if (error) {
        let updatedEmailThreadFormData = { ...emailThreadData };
        if (!isFolderView) {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            associationInstancesPopUp: [],
            isLoadingAssociationInstancesPopUp: false,
          };
        } else {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            folderInstancesPopUp: [],
            isLoadingFolderInstancesPopUp: false,
          };
        }
        updateEmailThreadFormData(updatedEmailThreadFormData);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|email-thread-upload|EmailThreadUpload - getInstancesPopup",
            callerApi:
              "api/Instance/GetFilteredBACTInstances?entityName=&parentEntityName=&parentId=&pageNumber=&pageSize=",
          },
        });
      }
    } else {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: pageSize,
        errMsg: errorMsg,
        searchTerm: searchTerm,
      };
      const instanceResponse = await instanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getInstances(data),
      });
      const { response, error } = instanceResponse;
      if (response) {
        let updatedEmailThreadFormData = { ...emailThreadData };
        if (!isFolderView) {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            associationInstancesPopUp: response,
            isLoadingAssociationInstancesPopUp: false,
          };
        } else {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            folderInstancesPopUp: response,
            isLoadingFolderInstancesPopUp: false,
          };
        }
        updateEmailThreadFormData(updatedEmailThreadFormData);
      }
      if (error) {
        let updatedEmailThreadFormData = { ...emailThreadData };
        if (!isFolderView) {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            associationInstancesPopUp: [],
            isLoadingAssociationInstances: false,
          };
        } else {
          updatedEmailThreadFormData = {
            ...updatedEmailThreadFormData,
            folderInstancesPopUp: [],
            isLoadingFolderInstancesPopUp: false,
          };
        }
        updateEmailThreadFormData(updatedEmailThreadFormData);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|email-thread-upload|EmailThreadUpload - getInstancesPopup",
            callerApi: "api/Agreement/FindAgreements?contractType=&page=&pageSize=",
          },
        });
      }
    }
  };

  const onChangeLocation = (data) => {
    const updatedEmailThreadFormData = { ...INITIAL_FORM_DATA, selectedAssociation: data };
    updateEmailThreadFormData(updatedEmailThreadFormData);
  };

  const onUploadChangeType = (_e, option) => {
    const selectedAssociation = emailThreadFormData.selectedAssociation;
    let updatedEmailThreadFormData = {
      ...INITIAL_FORM_DATA,
      selectedAssociation: selectedAssociation,
      selectedUploadType: option.key,
    };
    if (option.key === TEXT.NEW_DOCUMENT) {
      updatedEmailThreadFormData = { ...updatedEmailThreadFormData, newDocumentName: singleEmailData.subject || "" };
      updateEmailThreadFormData(updatedEmailThreadFormData);
    }
    if (option.key === TEXT.REPLACE_EXISTING) {
      if (!isFolderView) {
        updatedEmailThreadFormData = { ...updatedEmailThreadFormData, isLoadingAssociationInstances: true };
      } else {
        updatedEmailThreadFormData = { ...updatedEmailThreadFormData, isLoadingFolderInstances: true };
      }
      updateEmailThreadFormData(updatedEmailThreadFormData);
      getInstances(updatedEmailThreadFormData);
    }
    if (option.key === TEXT.SKIP) {
      updatedEmailThreadFormData = { ...updatedEmailThreadFormData };
      updateEmailThreadFormData(updatedEmailThreadFormData);
    }
  };

  const onChangeNewDocument = (_e, value) => {
    const selectedAssociation = emailThreadFormData.selectedAssociation;
    const selectedUploadType = emailThreadFormData.selectedUploadType;
    const updatedEmailThreadFormData = {
      ...INITIAL_FORM_DATA,
      selectedAssociation: selectedAssociation,
      selectedUploadType: selectedUploadType,
      newDocumentName: value,
    };
    updateEmailThreadFormData(updatedEmailThreadFormData);
  };

  const onFolderClickedNewDocument = (data) => {
    const selectedAssociation = emailThreadFormData.selectedAssociation;
    const selectedUploadType = emailThreadFormData.selectedUploadType;
    const newDocumentName = emailThreadFormData.newDocumentName;
    let updatedEmailThreadFormData = {
      ...INITIAL_FORM_DATA,
      selectedAssociation: selectedAssociation,
      selectedUploadType: selectedUploadType,
      newDocumentName: newDocumentName,
    };
    if (emailThreadFormData.selectedFolderNewDocument && emailThreadFormData.selectedFolderNewDocument.id === data.id) {
      updatedEmailThreadFormData = {
        ...updatedEmailThreadFormData,
        selectedFolderNewDocument: null,
      };
    } else {
      updatedEmailThreadFormData = {
        ...updatedEmailThreadFormData,
        selectedFolderNewDocument: data,
      };
    }
    updateEmailThreadFormData(updatedEmailThreadFormData);
  };

  const onFolderClickedReplaceExisting = (data) => {
    const selectedAssociation = emailThreadFormData.selectedAssociation;
    const selectedUploadType = emailThreadFormData.selectedUploadType;
    const folderInstances = emailThreadFormData.folderInstances;
    let updatedEmailThreadFormData = {
      ...INITIAL_FORM_DATA,
      selectedAssociation: selectedAssociation,
      selectedUploadType: selectedUploadType,
      folderInstances: folderInstances,
    };
    if (emailThreadFormData.selectedFolderNewDocument && emailThreadFormData.selectedFolderNewDocument.id === data.id) {
      updatedEmailThreadFormData = {
        ...updatedEmailThreadFormData,
        selectedFolderReplaceExisting: null,
      };
    } else {
      updatedEmailThreadFormData = {
        ...updatedEmailThreadFormData,
        selectedFolderReplaceExisting: data,
      };
    }
    updateEmailThreadFormData(updatedEmailThreadFormData);
  };

  const onAssociationInstanceClicked = (data) => {
    const selectedAssociation = emailThreadFormData.selectedAssociation;
    const selectedUploadType = emailThreadFormData.selectedUploadType;
    const associationInstances = emailThreadFormData.associationInstances;
    let updatedEmailThreadFormData = {
      ...INITIAL_FORM_DATA,
      selectedAssociation: selectedAssociation,
      selectedUploadType: selectedUploadType,
      associationInstances: associationInstances,
    };
    if (
      emailThreadFormData.replaceAssociationInstance &&
      emailThreadFormData.replaceAssociationInstance.EntityInstanceId === data.EntityInstanceId
    ) {
      updatedEmailThreadFormData = {
        ...updatedEmailThreadFormData,
        replaceAssociationInstance: null,
        replaceAssociationInstancePopup: null,
      };
    } else {
      updatedEmailThreadFormData = {
        ...updatedEmailThreadFormData,
        replaceAssociationInstance: data,
        replaceAssociationInstancePopup: data,
      };
    }
    updateEmailThreadFormData(updatedEmailThreadFormData);
  };

  const onFolderInstanceClicked = (data) => {
    const selectedAssociation = emailThreadFormData.selectedAssociation;
    const selectedUploadType = emailThreadFormData.selectedUploadType;
    const folderInstances = emailThreadFormData.folderInstances;
    const selectedFolderReplaceExisting = emailThreadFormData.selectedFolderReplaceExisting;
    let updatedEmailThreadFormData = {
      ...INITIAL_FORM_DATA,
      selectedAssociation: selectedAssociation,
      selectedUploadType: selectedUploadType,
      folderInstances: folderInstances,
      selectedFolderReplaceExisting: selectedFolderReplaceExisting,
    };
    if (
      emailThreadFormData.replaceFolderInstance &&
      emailThreadFormData.replaceFolderInstance.EntityInstanceId === data.EntityInstanceId
    ) {
      updatedEmailThreadFormData = {
        ...updatedEmailThreadFormData,
        replaceFolderInstance: null,
        replaceFolderInstancePopup: null,
      };
    } else {
      updatedEmailThreadFormData = {
        ...updatedEmailThreadFormData,
        replaceFolderInstance: data,
        replaceFolderInstancePopup: data,
      };
    }
    updateEmailThreadFormData(updatedEmailThreadFormData);
  };

  const onShowMoreClickedAssociationInstances = () => {
    const updatedEmailThreadFormData = {
      ...INITIAL_FORM_DATA,
      selectedAssociation: emailThreadFormData.selectedAssociation,
      selectedUploadType: emailThreadFormData.selectedUploadType,
      associationInstances: emailThreadFormData.associationInstances,
      replaceAssociationInstance: emailThreadFormData.replaceAssociationInstance,
      replaceAssociationInstancePopup: emailThreadFormData.replaceAssociationInstancePopup,
      showAssociationInstancesPopUp: true,
      isLoadingAssociationInstancesPopUp: true,
    };
    updateEmailThreadFormData(updatedEmailThreadFormData);
    getInstancesPopup(updatedEmailThreadFormData);
  };

  const onAssociationInstanceInPopupClicked = (data) => {
    if (
      emailThreadFormData &&
      emailThreadFormData.replaceAssociationInstancePopup &&
      emailThreadFormData.replaceAssociationInstancePopup.EntityInstanceId === data.EntityInstanceId
    ) {
      const updatedEmailThreadFormData = {
        ...emailThreadFormData,
        replaceAssociationInstancePopup: null,
      };
      updateEmailThreadFormData(updatedEmailThreadFormData);
    } else {
      const updatedEmailThreadFormData = {
        ...emailThreadFormData,
        replaceAssociationInstancePopup: data,
      };
      updateEmailThreadFormData(updatedEmailThreadFormData);
    }
  };

  const associationInstancePopupDoneClicked = () => {
    let updatedEmailThreadFormData = {
      ...emailThreadFormData,
      replaceAssociationInstance: emailThreadFormData.replaceAssociationInstancePopup,
      showAssociationInstancesPopUp: false,
    };
    const instances = emailThreadFormData.associationInstances;
    const replaceInstance = emailThreadFormData.replaceAssociationInstancePopup;
    const ifInstancePresentInTen = instances.filter(
      (instance) => instance.EntityInstanceId === replaceInstance.EntityInstanceId
    );
    if (ifInstancePresentInTen && ifInstancePresentInTen.length === 0) {
      const updatedInstances = [...instances];
      updatedInstances.unshift(replaceInstance);
      updatedEmailThreadFormData = {
        ...updatedEmailThreadFormData,
        associationInstances: updatedInstances,
      };
    }
    updateEmailThreadFormData(updatedEmailThreadFormData);
  };

  const hideAssociationInstancesPopup = () => {
    const updatedEmailThreadFormData = {
      ...emailThreadFormData,
      showAssociationInstancesPopUp: false,
      replaceAssociationInstancePopup: emailThreadFormData.replaceAssociationInstance,
    };
    updateEmailThreadFormData(updatedEmailThreadFormData);
  };

  const fetchAssociationInstancesOnSearch = (searchText = "") => {
    const updatedEmailThreadFormData = {
      ...emailThreadFormData,
      isLoadingAssociationInstancesPopUp: true,
      replaceAssociationInstance: null,
      replaceAssociationInstancePopup: null,
    };
    updateEmailThreadFormData(updatedEmailThreadFormData);
    getInstancesPopup(updatedEmailThreadFormData, searchText);
  };

  const onShowMoreClickedFolderInstances = () => {};

  const formatGroupLabel = (data) => {
    if (data.label) {
      return (
        <div className="format-group-label-single-email-upload">
          <span>{data.label}</span>
        </div>
      );
    }
    return null;
  };

  const groupedLocation = [
    {
      label: TEXT.ASSOCIATED_DOCUMENTS,
      options: associationData,
    },
  ];

  const uploadOptions: IChoiceGroupOption[] = [
    {
      key: TEXT.NEW_DOCUMENT,
      text: TEXT.NEW_DOCUMENT,
      disabled: emailThreadFormData.selectedAssociation ? false : true,
      onRenderLabel: (p) => (
        <span id={p.id + TEXT.NEW_DOCUMENT} className="ms-ChoiceFieldLabel" style={uploadOptionsRenderLabelStyle}>
          {p.text}
        </span>
      ),
      styles: uploadOptionsStyle,
    },
    {
      key: TEXT.REPLACE_EXISTING,
      text: TEXT.REPLACE_EXISTING,
      disabled: emailThreadFormData.selectedAssociation ? false : true,
      onRenderLabel: (p) => (
        <span id={p.id + TEXT.REPLACE_EXISTING} className="ms-ChoiceFieldLabel" style={uploadOptionsRenderLabelStyle}>
          {p.text}
        </span>
      ),
      styles: uploadOptionsStyle,
    },
    {
      key: TEXT.SKIP,
      text: TEXT.SKIP,
      disabled: emailThreadFormData.selectedAssociation ? false : true,
      onRenderLabel: (p) => (
        <span id={p.id + TEXT.SKIP} className="ms-ChoiceFieldLabel" style={uploadOptionsRenderLabelStyle}>
          {p.text}
        </span>
      ),
      styles: uploadOptionsStyle,
    },
  ];

  return (
    <div className="email-thread-upload-from-empty-single-email-upload">
      <div className="location-container-from-empty">
        <label htmlFor="location-email-thread-add-to-ici">{TEXT.ADD_TO_LOCATION}</label>
        <div className="location-container-from-empty-a11y" tabIndex={1}>
          <Select
            aria-labelledby="location-email-thread-add-to-ici"
            inputId="location-email-thread-add-to-ici"
            name="location-email-thread-add-to-ici"
            placeholder={TEXT.PLACEHOLDER_SELECT_LOCATION}
            options={groupedLocation}
            formatGroupLabel={formatGroupLabel}
            styles={locationDropdownStyles}
            isClearable
            value={emailThreadFormData.selectedAssociation}
            onChange={(option) => onChangeLocation(option)}
            noOptionsMessage={() => <p className="empty-message">{TEXT.NO_LOCATIONS_FOUND}</p>}
            components={{
              IndicatorSeparator: () => null,
            }}
          />
        </div>
      </div>
      <div className="choice-group-container-from-empty">
        <ChoiceGroup
          options={uploadOptions}
          onChange={onUploadChangeType}
          selectedKey={emailThreadFormData.selectedUploadType}
        />
      </div>
      {emailThreadFormData.selectedUploadType === TEXT.NEW_DOCUMENT ? (
        <div className="new-document-container-from-empty">
          {!isFolderView ? (
            <React.Fragment>
              <label htmlFor="name-of-document-email-thread">{TEXT.NAME_OF_DOCUMENT}</label>
              <div className="text-field-container-from-empty">
                <TextField
                  value={emailThreadFormData.newDocumentName}
                  onChange={onChangeNewDocument}
                  aria-labelledby="name-of-document-email-thread"
                  name="name-of-document-email-thread"
                  id="name-of-document-email-thread"
                />
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <label>{TEXT.DOCUMENT_LOCATION}</label>
              <FolderListing
                selectedData={emailThreadFormData.selectedFolderNewDocument}
                onFolderClick={onFolderClickedNewDocument}
              />
            </React.Fragment>
          )}
        </div>
      ) : null}
      {emailThreadFormData.selectedUploadType === TEXT.REPLACE_EXISTING ? (
        <div className="replace-existing-container-from-empty">
          {!isFolderView ? (
            <InstancesListing
              isLoading={emailThreadFormData.isLoadingAssociationInstances}
              loadingMsg={TEXT.LOADING_ASSOCIATION_INSTANCES}
              emptyMsg={TEXT.NO_ASSOCIATION_INSTANCES_FOUND}
              instances={emailThreadFormData.associationInstances}
              onInstanceClicked={onAssociationInstanceClicked}
              selectedInstance={emailThreadFormData.replaceAssociationInstance}
              showMoreClicked={onShowMoreClickedAssociationInstances}
            />
          ) : (
            <React.Fragment>
              <label>{TEXT.DOCUMENT_LOCATION}</label>
              <FolderListing
                selectedData={emailThreadFormData.selectedFolderReplaceExisting}
                onFolderClick={onFolderClickedReplaceExisting}
              />
              {emailThreadFormData.selectedFolderReplaceExisting ? (
                <InstancesListing
                  isLoading={emailThreadFormData.isLoadingFolderInstances}
                  loadingMsg={TEXT.LOADING_FOLDER_INSTANCES}
                  emptyMsg={TEXT.NO_FOLDER_INSTANCES_FOUND}
                  instances={emailThreadFormData.folderInstances.filter(
                    (instance) => instance.ICMFolderReference === emailThreadFormData.selectedFolderReplaceExisting.id
                  )}
                  onInstanceClicked={onFolderInstanceClicked}
                  selectedInstance={emailThreadFormData.replaceFolderInstance}
                  showMoreClicked={onShowMoreClickedFolderInstances}
                />
              ) : null}
            </React.Fragment>
          )}
        </div>
      ) : null}
      {emailThreadFormData ? (
        <Modal show={emailThreadFormData.showAssociationInstancesPopUp}>
          <InstancesListingPopUp
            isLoading={emailThreadFormData.isLoadingAssociationInstancesPopUp}
            loadingMsg={TEXT.LOADING_ASSOCIATION_INSTANCES}
            emptyMsg={TEXT.NO_ASSOCIATION_INSTANCES_FOUND}
            instances={emailThreadFormData.associationInstancesPopUp}
            onInstanceClicked={onAssociationInstanceInPopupClicked}
            selectedInstance={emailThreadFormData.replaceAssociationInstancePopup}
            hidePopup={hideAssociationInstancesPopup}
            onDoneClicked={associationInstancePopupDoneClicked}
            onSearch={fetchAssociationInstancesOnSearch}
          />
        </Modal>
      ) : null}
    </div>
  );
};

const locationDropdownStyles = {
  menu: (styles: any) => ({
    ...styles,
    border: "0.0625rem solid #a6a6a6",
    boxShadow: "none",
    marginTop: "-0.25rem",
  }),
  placeholder: (styles: any) => ({
    ...styles,
    backgroundColor: "#fff",
    color: "#000",
  }),
  control: (styles: any) => ({
    ...styles,
    marginTop: "0.125rem",
    backgroundColor: "#fff",
    border: "0.0625rem solid #a6a6a6",
    boxShadow: "none",
    "&:hover": {
      border: "0.0625rem solid #a6a6a6",
    },
    fontSize: "0.75rem",
    borderRadius: "0",
    height: 32,
    minHeight: 32,
  }),
  valueContainer: (styles) => ({
    ...styles,
    height: 32,
    padding: 4,
    marginTop: "-0.125rem",
  }),
  indicatorsContainer: (styles) => ({
    ...styles,
    height: 32,
    marginTop: "-0.0625rem",
  }),
  option: (styles: { [x: string]: any }, { isFocused, isSelected }: any) => {
    return {
      ...styles,
      backgroundColor: isSelected ? "#0078d4" : isFocused ? "#f3f2f1" : "#fff",
      outline: isFocused ? "1px solid #00a2ed" : null,
      cursor: isFocused ? "pointer" : "default",
      ":active": {
        ...styles[":active"],
        backgroundColor: "#0078d4",
      },
      borderRadius: "0.25rem",
      fontSize: "0.75rem",
    };
  },
  group: (styles) => {
    return {
      ...styles,
      backgroundColor: "#ebeae8",
      padding: 0,
      marginTop: "-0.25rem",
    };
  },
};

const uploadOptionsStyle = {
  root: { display: "inline" },
  choiceFieldWrapper: { margin: "0.25rem 0" },
};

const uploadOptionsRenderLabelStyle = {
  fontSize: "0.75rem",
  margin: "0.125rem 0 0.375rem",
  color: "#666",
};
